import { supabase } from '../lib/supabase';



export const loanService = {
    async getAll(filters = {}) {
        let query = supabase
            .from('loans')
            .select(`
                *,
                covenants (*)
            `)
            .order('created_at', { ascending: false });

        if (filters.status && filters.status !== 'all') {
            query = query.eq('status', filters.status);
        }
        if (filters.sector && filters.sector !== 'all') {
            query = query.eq('sector', filters.sector);
        }
        if (filters.country && filters.country !== 'all') {
            query = query.eq('country', filters.country);
        }
        if (filters.search) {
            query = query.or(`borrower.ilike.%${filters.search}%,reference.ilike.%${filters.search}%`);
        }

        const { data, error } = await query;
        if (error) throw error;

        return data.map(loan => ({
            ...loan,
            overallStatus: loan.covenants?.some(c => c.status === 'breach') ? 'critical' :
                          loan.covenants?.some(c => c.status === 'at_risk') ? 'warning' : 'ok'
        }));
    },

    async getById(id) {
        const { data, error } = await supabase
            .from('loans')
            .select(`
                *,
                covenants (*),
                loan_participants (*)
            `)
            .eq('id', id)
            .single();

        if (error) throw error;
        return data;
    },

    async create(loanData) {
        const { data, error } = await supabase
            .from('loans')
            .insert({
                reference: loanData.reference || `LN-${Date.now()}`,
                facility_name: loanData.facility_name || loanData.borrower,
                borrower: loanData.borrower,
                borrower_id: loanData.borrower_id,
                lead_arranger: loanData.lead_arranger || loanData.leadArranger,
                bank: loanData.bank,
                amount: loanData.amount,
                currency: loanData.currency || 'EUR',
                interest_rate: loanData.interest_rate || loanData.interestRate,
                start_date: loanData.start_date || loanData.startDate,
                maturity_date: loanData.maturity_date || loanData.maturityDate,
                next_reporting_date: loanData.next_reporting_date || loanData.nextReportingDate,
                sector: loanData.sector,
                country: loanData.country,
                status: ['active', 'archived', 'closed', 'defaulted'].includes(loanData.status) ? loanData.status : 'active',
                compliance_status: 'compliant',
                notes: loanData.notes,
                created_by: loanData.created_by
            })
            .select()
            .single();

        if (error) throw error;
        return data;
    },

    async update(id, loanData) {
        const { data, error } = await supabase
            .from('loans')
            .update({
                ...loanData,
                updated_at: new Date().toISOString()
            })
            .eq('id', id)
            .select()
            .single();

        if (error) throw error;
        return data;
    },

    async delete(id) {
        const { error } = await supabase
            .from('loans')
            .delete()
            .eq('id', id);

        if (error) throw error;
        return true;
    },

    async getStats() {
        const { data: loans, error } = await supabase
            .from('loans')
            .select('amount, status, compliance_status');

        if (error) throw error;

        const { data: covenants } = await supabase
            .from('covenants')
            .select('status');

        const { data: alerts } = await supabase
            .from('alerts')
            .select('id')
            .eq('is_read', false)
            .eq('is_archived', false);

        const totalAmount = loans?.reduce((sum, l) => sum + (parseFloat(l.amount) || 0), 0) || 0;
        const compliant = covenants?.filter(c => c.status === 'compliant').length || 0;
        const atRisk = covenants?.filter(c => c.status === 'at_risk').length || 0;
        const breached = covenants?.filter(c => c.status === 'breach').length || 0;

        return {
            totalLoans: loans?.length || 0,
            totalAmount,
            activeCovenants: covenants?.length || 0,
            compliantCovenants: compliant,
            atRiskCovenants: atRisk,
            breachedCovenants: breached,
            unreadAlerts: alerts?.length || 0,
            complianceRate: covenants?.length > 0 ? Math.round((compliant / covenants.length) * 100) : 100
        };
    },

    async getSectors() {
        const { data, error } = await supabase
            .from('loans')
            .select('sector')
            .not('sector', 'is', null);

        if (error) throw error;
        return [...new Set(data.map(d => d.sector))];
    },

    async getCountries() {
        const { data, error } = await supabase
            .from('loans')
            .select('country')
            .not('country', 'is', null);

        if (error) throw error;
        return [...new Set(data.map(d => d.country))];
    }
};



export const covenantService = {
    async getByLoanId(loanId) {
        const { data, error } = await supabase
            .from('covenants')
            .select('*')
            .eq('loan_id', loanId)
            .order('name');

        if (error) throw error;
        return data;
    },

    async getById(id) {
        const { data, error } = await supabase
            .from('covenants')
            .select('*')
            .eq('id', id)
            .single();

        if (error) throw error;
        return data;
    },

    async create(covenantData) {
        let status = 'compliant';
        if (covenantData.current_value && covenantData.threshold) {
            const current = parseFloat(covenantData.current_value);
            const threshold = parseFloat(covenantData.threshold);
            const operator = covenantData.operator || '<=';

            let isBreached = false;
            let warningThreshold = threshold * 0.9;

            switch (operator) {
                case '<=':
                    isBreached = current > threshold;
                    warningThreshold = threshold * 0.9;
                    break;
                case '>=':
                    isBreached = current < threshold;
                    warningThreshold = threshold * 1.1;
                    break;
                case '<':
                    isBreached = current >= threshold;
                    break;
                case '>':
                    isBreached = current <= threshold;
                    break;
                default:
                    isBreached = current !== threshold;
            }

            if (isBreached) {
                status = 'breach';
            } else if (operator === '<=' && current > warningThreshold) {
                status = 'at_risk';
            } else if (operator === '>=' && current < warningThreshold) {
                status = 'at_risk';
            }
        }

        const { data, error } = await supabase
            .from('covenants')
            .insert({
                loan_id: covenantData.loan_id || covenantData.loanId,
                name: covenantData.name,
                type: covenantData.type || 'financial',
                category: covenantData.category || 'leverage',
                description: covenantData.description,
                threshold: covenantData.threshold,
                current_value: covenantData.current_value || covenantData.currentValue,
                operator: covenantData.operator || '<=',
                frequency: covenantData.frequency || 'quarterly',
                next_test_date: covenantData.next_test_date || covenantData.nextTestDate,
                status
            })
            .select()
            .single();

        if (error) throw error;

        if (status === 'at_risk' || status === 'breach') {
            await alertService.create({
                loan_id: covenantData.loan_id || covenantData.loanId,
                covenant_id: data.id,
                type: status === 'breach' ? 'critical' : 'warning',
                category: status === 'breach' ? 'breach' : 'approaching',
                title: `Covenant ${status === 'breach' ? 'breach' : 'warning'}: ${covenantData.name}`,
                message: `Current value ${covenantData.current_value} ${status === 'breach' ? 'exceeds' : 'approaching'} threshold ${covenantData.threshold}`
            });
        }

        return data;
    },

    async updateValue(id, value, userId = null, notes = null) {
        const covenant = await this.getById(id);

        let status = 'compliant';
        const current = parseFloat(value);
        const threshold = parseFloat(covenant.threshold);
        const operator = covenant.operator || '<=';

        let isBreached = false;

        switch (operator) {
            case '<=':
                isBreached = current > threshold;
                if (!isBreached && current > threshold * 0.9) status = 'at_risk';
                break;
            case '>=':
                isBreached = current < threshold;
                if (!isBreached && current < threshold * 1.1) status = 'at_risk';
                break;
            default:
                isBreached = current !== threshold;
        }

        if (isBreached) status = 'breach';

        const { data, error } = await supabase
            .from('covenants')
            .update({
                current_value: value,
                status,
                updated_at: new Date().toISOString()
            })
            .eq('id', id)
            .select();

        if (error) throw error;
        if (!data || data.length === 0) throw new Error('Covenant not found or update not permitted');


        await supabase.from('covenant_history').insert({
            covenant_id: id,
            value: current,
            threshold,
            status,
            headroom: threshold - current,
            recorded_by: userId,
            notes
        });


        await this.updateLoanStatus(covenant.loan_id);

        if ((status === 'at_risk' || status === 'breach') && status !== covenant.status) {
            await alertService.create({
                loan_id: covenant.loan_id,
                covenant_id: id,
                type: status === 'breach' ? 'critical' : 'warning',
                category: status === 'breach' ? 'breach' : 'approaching',
                title: `Covenant ${status === 'breach' ? 'breach' : 'warning'}: ${covenant.name}`,
                message: `Current value ${value} ${status === 'breach' ? 'exceeds' : 'approaching'} threshold ${covenant.threshold}`
            });
        }

        return data;
    },

    async updateLoanStatus(loanId) {
        const { data: covenants } = await supabase
            .from('covenants')
            .select('status')
            .eq('loan_id', loanId);

        let newStatus = 'compliant';
        if (covenants?.some(c => c.status === 'breach')) {
            newStatus = 'non_compliant';
        } else if (covenants?.some(c => c.status === 'at_risk')) {
            newStatus = 'at_risk';
        }

        await supabase
            .from('loans')
            .update({
                compliance_status: newStatus,
                updated_at: new Date().toISOString()
            })
            .eq('id', loanId);
    },

    async delete(id) {
        const { error } = await supabase
            .from('covenants')
            .delete()
            .eq('id', id);

        if (error) throw error;
        return true;
    }
};



export const alertService = {
    async getAll(filters = {}) {
        let query = supabase
            .from('alerts')
            .select(`
                *,
                loans (borrower, reference)
            `)
            .eq('is_archived', false)
            .order('created_at', { ascending: false });

        if (filters.type && filters.type !== 'all') {
            query = query.eq('type', filters.type);
        }
        if (filters.isRead !== undefined) {
            query = query.eq('is_read', filters.isRead);
        }

        const { data, error } = await query;
        if (error) throw error;
        return data;
    },

    async create(alertData) {
        const { data, error } = await supabase
            .from('alerts')
            .insert({
                loan_id: alertData.loan_id || alertData.loanId,
                covenant_id: alertData.covenant_id || alertData.covenantId,
                type: alertData.type,
                category: alertData.category,
                title: alertData.title,
                message: alertData.message,
                covenant_name: alertData.covenant_name,
                current_value: alertData.current_value,
                threshold: alertData.threshold
            })
            .select()
            .single();

        if (error) throw error;
        return data;
    },

    async markAsRead(id) {
        const { data, error } = await supabase
            .from('alerts')
            .update({ is_read: true, read_at: new Date().toISOString() })
            .eq('id', id)
            .select()
            .single();

        if (error) throw error;
        return data;
    },

    async markAllAsRead() {
        const { error } = await supabase
            .from('alerts')
            .update({ is_read: true, read_at: new Date().toISOString() })
            .eq('is_read', false);

        if (error) throw error;
        return true;
    },

    async archive(id) {
        const { error } = await supabase
            .from('alerts')
            .update({ is_archived: true, archived_at: new Date().toISOString() })
            .eq('id', id);

        if (error) throw error;
        return true;
    }
};



export const waiverService = {
    generateReference() {
        const year = new Date().getFullYear();
        const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
        return `WAV-${year}-${random}`;
    },

    async getAll(filters = {}) {
        let query = supabase
            .from('waivers')
            .select(`
                *,
                loans (borrower, reference),
                waiver_comments (*),
                waiver_documents (*),
                waiver_timeline (*)
            `)
            .order('created_at', { ascending: false });

        if (filters.status && filters.status !== 'all') {
            query = query.eq('status', filters.status);
        }
        if (filters.loanId) {
            query = query.eq('loan_id', filters.loanId);
        }

        const { data, error } = await query;
        if (error) throw error;

        return data.map(w => ({
            ...w,
            id: w.reference,
            dbId: w.id,
            borrower: w.loans?.borrower,
            loanReference: w.loans?.reference,
            covenantName: w.covenant_name,
            covenantType: w.covenant_type,
            currentValue: w.current_value,
            requestedWaiver: w.requested_waiver,
            requestedBy: w.requested_by_name,
            requiredMajority: w.required_majority,
            currentApproval: w.current_approval,
            totalLenders: w.total_lenders,
            votesReceived: w.votes_received,
            votesFor: w.votes_for,
            votesAgainst: w.votes_against,
            votingDeadline: w.voting_deadline,
            comments: w.waiver_comments?.map(c => ({
                id: c.id,
                user: c.user_name,
                role: c.user_role,
                text: c.text,
                date: c.created_at
            })),
            documents: w.waiver_documents,
            timeline: w.waiver_timeline
        }));
    },

    async getById(id) {
        const { data, error } = await supabase
            .from('waivers')
            .select(`
                *,
                loans (borrower, reference),
                waiver_comments (*),
                waiver_documents (*),
                waiver_timeline (*),
                waiver_votes (*)
            `)
            .eq('id', id)
            .single();

        if (error) throw error;

        return {
            ...data,
            id: data.reference,
            dbId: data.id,
            borrower: data.loans?.borrower,
            covenantName: data.covenant_name,
            covenantType: data.covenant_type,
            currentValue: data.current_value,
            requestedWaiver: data.requested_waiver,
            requestedBy: data.requested_by_name,
            requiredMajority: data.required_majority,
            currentApproval: data.current_approval,
            totalLenders: data.total_lenders,
            votesReceived: data.votes_received,
            votesFor: data.votes_for,
            votesAgainst: data.votes_against,
            votingDeadline: data.voting_deadline,
            comments: data.waiver_comments?.map(c => ({
                id: c.id,
                user: c.user_name,
                role: c.user_role,
                text: c.text,
                date: c.created_at
            })),
            documents: data.waiver_documents,
            timeline: data.waiver_timeline,
            votes: data.waiver_votes
        };
    },

    async create(waiverData, userId, userName) {
        const reference = this.generateReference();

        const { data, error } = await supabase
            .from('waivers')
            .insert({
                reference,
                loan_id: waiverData.loanId || waiverData.loan_id,
                covenant_id: waiverData.covenantId || waiverData.covenant_id,
                covenant_name: waiverData.covenantName || waiverData.covenant_name,
                covenant_type: waiverData.covenantType || waiverData.covenant_type || 'financial',
                current_value: waiverData.currentValue || waiverData.current_value,
                threshold: waiverData.threshold,
                requested_waiver: waiverData.requestedWaiver || waiverData.requested_waiver,
                reason: waiverData.reason,
                status: waiverData.status || 'draft',
                requested_by: null,
                requested_by_name: userName || 'Unknown User',
                required_majority: waiverData.requiredMajority || waiverData.required_majority || 66.67
            })
            .select()
            .single();

        if (error) throw error;

        await this.addTimelineEvent(data.id, 'created', 'Request Created', 'Waiver request created', null, userName);

        return {
            ...data,
            id: data.reference,
            dbId: data.id
        };
    },

    async updateStatus(id, status, userId, userName) {
        const { data, error } = await supabase
            .from('waivers')
            .update({
                status,
                updated_at: new Date().toISOString()
            })
            .eq('id', id)
            .select()
            .single();

        if (error) throw error;

        const eventTitles = {
            pending_review: 'Submitted for Review',
            negotiating: 'Negotiation Started',
            under_voting: 'Voting Opened',
            approved: 'Waiver Approved',
            rejected: 'Waiver Rejected',
            withdrawn: 'Request Withdrawn'
        };

        await this.addTimelineEvent(id, status, eventTitles[status] || status, `Status changed to ${status}`, userId, userName);

        return data;
    },

    async openForVoting(id, userId, userName, deadline = null) {
        const votingDeadline = deadline || new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

        const { data, error } = await supabase
            .from('waivers')
            .update({
                status: 'under_voting',
                voting_deadline: votingDeadline,
                updated_at: new Date().toISOString()
            })
            .eq('id', id)
            .select()
            .single();

        if (error) throw error;

        await this.addTimelineEvent(id, 'voting_opened', 'Voting Opened', `Voting deadline: ${votingDeadline}`, userId, userName);

        return data;
    },

    async castVote(waiverId, lenderId, lenderName, vote, participationPercentage, comment = null) {
        const { data, error } = await supabase
            .from('waiver_votes')
            .insert({
                waiver_id: waiverId,
                lender_id: lenderId,
                lender_name: lenderName,
                vote,
                participation_percentage: participationPercentage,
                comment
            })
            .select()
            .single();

        if (error) throw error;


        const { data: votes } = await supabase
            .from('waiver_votes')
            .select('*')
            .eq('waiver_id', waiverId);

        const votesFor = votes.filter(v => v.vote === 'approve').length;
        const votesAgainst = votes.filter(v => v.vote === 'reject').length;
        const approvalPercentage = votes
            .filter(v => v.vote === 'approve')
            .reduce((sum, v) => sum + (v.participation_percentage || 0), 0);

        await supabase
            .from('waivers')
            .update({
                votes_received: votes.length,
                votes_for: votesFor,
                votes_against: votesAgainst,
                current_approval: approvalPercentage
            })
            .eq('id', waiverId);

        await this.addTimelineEvent(waiverId, 'vote_cast', 'Vote Cast', `${lenderName} voted: ${vote}`, lenderId, lenderName);

        return data;
    },

    async addComment(waiverId, text, userId, userName, userRole) {
        const { data, error } = await supabase
            .from('waiver_comments')
            .insert({
                waiver_id: waiverId,
                user_id: null,
                user_name: userName || 'Unknown User',
                user_role: userRole || 'user',
                text
            })
            .select()
            .single();

        if (error) throw error;

        await this.addTimelineEvent(waiverId, 'comment_added', 'Comment Added', text.substring(0, 100), null, userName);

        return {
            id: data.id,
            user: data.user_name,
            role: data.user_role,
            text: data.text,
            date: data.created_at
        };
    },

    async addDocument(waiverId, documentData, userId) {
        const { data, error } = await supabase
            .from('waiver_documents')
            .insert({
                waiver_id: waiverId,
                name: documentData.name,
                file_path: documentData.filePath || documentData.file_path,
                file_size: documentData.fileSize || documentData.file_size,
                mime_type: documentData.mimeType || documentData.mime_type,
                uploaded_by: null
            })
            .select()
            .single();

        if (error) throw error;

        await this.addTimelineEvent(waiverId, 'document_uploaded', 'Document Uploaded', documentData.name, null, null);

        return data;
    },

    async addTimelineEvent(waiverId, eventType, eventTitle, eventDescription, userId, userName) {
        const { error } = await supabase
            .from('waiver_timeline')
            .insert({
                waiver_id: waiverId,
                event_type: eventType,
                event_title: eventTitle,
                event_description: eventDescription,
                user_id: userId || null,
                user_name: userName
            });

        if (error) console.error('Error adding timeline event:', error);
    },

    async getStats() {
        const { data, error } = await supabase
            .from('waivers')
            .select('status');

        if (error) throw error;

        return {
            total: data?.length || 0,
            pending: data?.filter(w => w.status === 'pending_review').length || 0,
            voting: data?.filter(w => w.status === 'under_voting').length || 0,
            approved: data?.filter(w => w.status === 'approved').length || 0,
            rejected: data?.filter(w => w.status === 'rejected').length || 0,
            negotiating: data?.filter(w => w.status === 'negotiating').length || 0
        };
    }
};



export const participantService = {
    async getByLoanId(loanId) {
        const { data, error } = await supabase
            .from('loan_participants')
            .select(`
                *,
                profiles:lender_id (first_name, last_name, company)
            `)
            .eq('loan_id', loanId)
            .order('participation_percentage', { ascending: false });

        if (error) throw error;
        return data;
    },

    async add(participantData) {
        const { data, error } = await supabase
            .from('loan_participants')
            .insert({
                loan_id: participantData.loan_id,
                lender_id: participantData.lender_id,
                lender_name: participantData.lender_name,
                participation_amount: participantData.participation_amount,
                participation_percentage: participantData.participation_percentage,
                role: participantData.role || 'participant'
            })
            .select()
            .single();

        if (error) throw error;
        return data;
    },

    async remove(id) {
        const { error } = await supabase
            .from('loan_participants')
            .delete()
            .eq('id', id);

        if (error) throw error;
        return true;
    }
};



export const documentService = {
    async getByLoanId(loanId) {
        const { data, error } = await supabase
            .from('documents')
            .select('*')
            .eq('loan_id', loanId)
            .order('uploaded_at', { ascending: false });

        if (error) throw error;
        return data;
    },

    async upload(documentData) {
        const { data, error } = await supabase
            .from('documents')
            .insert({
                loan_id: documentData.loan_id,
                name: documentData.name,
                type: documentData.type || 'other',
                file_path: documentData.file_path,
                file_size: documentData.file_size,
                mime_type: documentData.mime_type,
                uploaded_by: documentData.uploaded_by
            })
            .select()
            .single();

        if (error) throw error;
        return data;
    },

    async delete(id) {
        const { error } = await supabase
            .from('documents')
            .delete()
            .eq('id', id);

        if (error) throw error;
        return true;
    }
};



export const activityLogService = {
    async getAll(filters = {}) {
        let query = supabase
            .from('activity_logs')
            .select(`
                *,
                profiles:user_id (first_name, last_name, email)
            `)
            .order('created_at', { ascending: false })
            .limit(100);

        if (filters.userId) {
            query = query.eq('user_id', filters.userId);
        }
        if (filters.type) {
            query = query.eq('type', filters.type);
        }
        if (filters.loanId) {
            query = query.eq('loan_id', filters.loanId);
        }

        const { data, error } = await query;
        if (error) throw error;
        return data;
    },

    async create(logData) {
        const { data, error } = await supabase
            .from('activity_logs')
            .insert({
                user_id: logData.user_id,
                loan_id: logData.loan_id,
                type: logData.type,
                category: logData.category,
                action: logData.action,
                message: logData.message,
                details: logData.details,
                ip_address: logData.ip_address,
                user_agent: logData.user_agent
            })
            .select()
            .single();

        if (error) throw error;
        return data;
    }
};

export default {
    loanService,
    covenantService,
    alertService,
    waiverService,
    participantService,
    documentService,
    activityLogService
};